import { ChangeDetectionStrategy, Component, OnInit, Signal, inject } from '@angular/core';
import { FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { NgIconComponent, provideIcons } from '@ng-icons/core';
import { lucideCircleAlert, lucideHouse } from '@ng-icons/lucide';

import { FocusOnlyRadioGroupDirective } from 'app/home/common/directives/focus-only-radio-group.directive';
import { ReadonlyRadioDirective } from 'app/home/common/directives/readonly-radio.directive';
import { BaseQuoteForm } from 'app/home/create-quote/form-config/quote-form-base';
import { QuoteFormContextDecorator } from 'app/home/create-quote/form-config/quote-form-decorator';
import { Lockable, LockedQuoteAlertComponent, WithLockableFields } from 'app/home/create-quote/form-locking';
import { ResidencyQuoteFormInput } from 'app/home/create-quote/forms/residency/residency.types';
import { FormCardComponent } from 'app/home/create-quote/forms/shared/form-card.component';
import { QuoteDraftState } from 'app/home/global-store/quote-draft/quote-draft.model';
import { QuoteFormOptionsStore } from 'app/home/global-store/quote-form-options';
import { FeatureFlagSignalsService } from 'app/shared/services/splitIO/feature-flag.service';

@Component({
  selector: 'app-residency',
  imports: [ReactiveFormsModule, FormCardComponent, NgIconComponent, ReadonlyRadioDirective, LockedQuoteAlertComponent, FocusOnlyRadioGroupDirective],
  providers: [provideIcons({ lucideHouse, lucideCircleAlert })],
  templateUrl: './residency.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  styles: [
    `
      :host {
        display: contents;
      }
    `,
  ],
})
@QuoteFormContextDecorator({
  legacyShape: '/quote/residency',
  isAutoAdvanced: true,
})
export class QuoteFormResidencyComponent extends BaseQuoteForm<ResidencyQuoteFormInput> implements OnInit, Lockable {
  private featureFlagSignalsService = inject(FeatureFlagSignalsService);
  public isAutoAdvanceEnabled = this.featureFlagSignalsService.isAutoAdvanceEnabled;

  private optionsStore = inject(QuoteFormOptionsStore);
  private _residencyDataIn = this.quoteDraftStore.residency();
  // residency value can be 0
  public formDataIn: ResidencyQuoteFormInput = { residency: this._residencyDataIn != null ? `${this._residencyDataIn}` : undefined };

  public residencyOptions = this.optionsStore.residency();

  public formDefinition = {
    residency: new FormControl<string | undefined>(undefined, Validators.required),
  };
  public residencyForm = new FormGroup(this.formDefinition);

  public override autoAdvanceControlRef = this.residencyForm.controls.residency;

  public ngOnInit() {
    if (this.formDataIn && this.isFieldLocked('residency')) {
      this.residencyForm.controls.residency.setValue(this.formDataIn.residency);
    }
  }

  public getFormControls() {
    return this.residencyForm.controls;
  }

  public isLocked: Signal<{ [key: string]: boolean }> = WithLockableFields.createIsLocked.call(this);
  public isFieldLocked = WithLockableFields.isFieldLocked;
  public allFieldsLocked = WithLockableFields.allFieldsLocked;

  private getFormValues(): Pick<QuoteDraftState, 'residency'> {
    const { controls } = this.residencyForm;
    const { value } = controls.residency;
    const formValues = {
      residency: value ? parseInt(value, 10) : undefined,
    };

    return formValues;
  }

  public handleSubmit(e: Event) {
    e.preventDefault();
    this.residencyForm.markAllAsTouched();
    if (this.residencyForm.valid) {
      if (this.allFieldsLocked()) {
        super.navigateForward();
      } else {
        super.saveFormData(this.getFormValues());
      }
    }
  }

  public showFormRequiredError(): boolean {
    if (this.isAutoAdvanceEnabled()) {
      return false;
    }
    const control = this.residencyForm.get('residency');
    return control ? control.invalid && control.touched : false;
  }
}
